import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, X, ArrowsClockwise } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_TABS = [
  { key: 'pending',  label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

const DeviceRequestRow = ({ req, onDecide, actionsAllowed }) => (
  <div className="border border-gray-200 rounded-lg p-3 bg-gray-50" data-testid={`device-approval-${req.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-gray-800">
          {req.handler_username || req.handler_id}
          <span className="text-gray-400 font-normal"> · device </span>
          <span className="text-gray-700">"{req.device_name || 'Unnamed device'}"</span>
        </div>
        <div className="mt-1 text-xs text-gray-600">
          {req.city || req.state ? `${req.city}${req.city && req.state ? ', ' : ''}${req.state} · ` : ''}
          {req.ip_address || 'unknown IP'}
        </div>
        <div className="text-[10px] text-gray-400 mt-1">
          Requested {req.requested_at ? new Date(req.requested_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
          {req.decided_at && <> · Reviewed {new Date(req.decided_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} by {req.decided_by || '-'}</>}
        </div>
      </div>
      {actionsAllowed && (
        <div className="flex flex-col gap-1 flex-shrink-0">
          <button
            onClick={() => onDecide(req, true)}
            className="px-2.5 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 flex items-center gap-1"
            data-testid={`device-approve-${req.id}`}
          ><Check size={12} /> Approve</button>
          <button
            onClick={() => onDecide(req, false)}
            className="px-2.5 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 flex items-center gap-1"
            data-testid={`device-reject-${req.id}`}
          ><X size={12} /> Reject</button>
        </div>
      )}
    </div>
  </div>
);

/**
 * Permissions Hub — device-login-approval only (point 46). The previous
 * Flags / Bank Edits / Message Deletes approval tabs were removed per spec;
 * this now holds the "new device restriction" toggle (point 10) plus the
 * queue of handler devices waiting on admin approval (point 45).
 */
const PermissionsHub = ({ adminId }) => {
  const [restrictionOn, setRestrictionOn] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    axios.get(`${API}/admin/settings/device-restriction`)
      .then(res => setRestrictionOn(!!res.data?.enabled))
      .catch(() => {});
  }, []);

  const toggleRestriction = async () => {
    setToggleLoading(true);
    try {
      const res = await axios.put(`${API}/admin/settings/device-restriction`, { enabled: !restrictionOn });
      setRestrictionOn(!!res.data?.enabled);
      toast.success(`New device restriction ${res.data?.enabled ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error('Failed to update setting');
    } finally {
      setToggleLoading(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/device-approvals`, { params: { status } });
      setRows(res.data?.items || []);
    } catch (e) { toast.error('Failed to load device requests'); }
    finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    const loadCount = async () => {
      try {
        const res = await axios.get(`${API}/admin/device-approvals`, { params: { status: 'pending' } });
        if (!cancelled) setPendingCount((res.data?.items || []).length);
      } catch (_) { /* silent */ }
    };
    loadCount();
    const t = setInterval(loadCount, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const decide = async (req, approve) => {
    try {
      await axios.post(`${API}/admin/device-approvals/${req.id}/decide`, { admin_id: adminId, approve });
      toast.success(`Device ${approve ? 'approved' : 'rejected'}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6" data-testid="permissions-hub">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <span className="text-xl">🔓</span> Permissions
        </h3>
      </div>

      <div className="flex items-center justify-between border border-gray-200 rounded-lg p-3 mb-4 bg-gray-50">
        <div>
          <div className="text-sm font-semibold text-gray-800">New device restriction</div>
          <div className="text-xs text-gray-500 mt-0.5">
            {restrictionOn
              ? 'ON — a handler logging in from an unrecognized device must submit a name and wait for your approval below.'
              : 'OFF — handlers can log in directly with ID + password on any device.'}
          </div>
        </div>
        <button
          onClick={toggleRestriction}
          disabled={toggleLoading}
          data-testid="device-restriction-toggle"
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${restrictionOn ? 'bg-blue-600' : 'bg-gray-300'} disabled:opacity-50`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${restrictionOn ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>

      <div className="flex items-center justify-between mb-2">
        <div className="flex gap-1.5">
          {STATUS_TABS.map(s => (
            <button
              key={s.key}
              onClick={() => setStatus(s.key)}
              data-testid={`device-approval-status-${s.key}`}
              className={`px-3 py-1 rounded text-xs font-semibold border transition flex items-center gap-1 ${
                status === s.key ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {s.label}
              {s.key === 'pending' && pendingCount > 0 && (
                <span className="min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                  {pendingCount > 99 ? '99+' : pendingCount}
                </span>
              )}
            </button>
          ))}
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="p-1.5 hover:bg-gray-100 rounded-full disabled:opacity-50"
          title="Refresh"
        ><ArrowsClockwise size={14} /></button>
      </div>

      {loading ? (
        <div className="py-6 text-center text-gray-400 text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="py-6 text-center text-gray-400 text-sm">No {status} device requests</div>
      ) : (
        <div className="space-y-2">
          {rows.map(r => (
            <DeviceRequestRow key={r.id} req={r} onDecide={decide} actionsAllowed={status === 'pending'} />
          ))}
        </div>
      )}
    </div>
  );
};

export default PermissionsHub;
